import { Flex } from "@components/index";
import LoadingFragment from "@fragments/Loading.fragment";
import { useGetNegExecution } from "@services/negExecution/hooks";
import { useMemo } from "react";
import { RegionalProgress, StepCard } from "./components";
import styles from "./NegExecution.module.scss";
import { extractRoundNumber } from "./negExecution.service";
import {
  extractRegionalName,
  extractStepNumber,
  isAllStepsCompleted,
  NEG_EXECUTION_STEPS,
  type NegExecutionTemplateStep,
  type NegExecutionTemplateSubStep,
} from "./negExecutionSteps.mock";

export interface NegExecutionFragmentProps {
  enabled?: boolean;
}

type MergedStep = {
  stepId: string;
  message: string;
  eventType: string;
  isProcessed: boolean;
  steps?: MergedSubStep[];
};

type MergedSubStep = {
  stepId: string;
  name: string;
  eventType: string;
  isProcessed: boolean;
};

/**
 * Processa sub-steps (regionais) para etapa 07
 * Extraído para função separada para reduzir aninhamento
 */
const mergeSubSteps = (
  templateSteps: NegExecutionTemplateSubStep[] | undefined,
  apiItem: Sentinel.NegExecution.Item | undefined
): MergedSubStep[] | undefined => {
  if (!templateSteps) return undefined;

  return templateSteps.map((subTemplate) => {
    const regionalName = subTemplate.stepId.replace("07-", "");
    const apiSubStep = apiItem?.steps?.find((s) => {
      const apiRegional = extractRegionalName(s.name);
      return apiRegional === regionalName;
    });

    return {
      stepId: subTemplate.stepId,
      name: subTemplate.name,
      eventType: apiSubStep?.eventType ?? "PENDING",
      isProcessed: Boolean(apiSubStep),
    };
  });
};

/**
 * Faz o merge de um template step com dados da API
 * Extraído para função separada para reduzir aninhamento
 */
const mergeStep = (
  template: NegExecutionTemplateStep,
  data: Sentinel.NegExecution.Response | undefined,
  allCompleted: boolean
): MergedStep => {
  // Caso especial: Fim da Rodada
  if (template.stepId === "FIM") {
    return {
      stepId: template.stepId,
      message: template.message,
      eventType: allCompleted ? "FINISH" : "PENDING",
      isProcessed: allCompleted,
      steps: undefined,
    };
  }

  // Busca a etapa correspondente na API
  const apiItem = data?.find((item) => {
    const stepNum = extractStepNumber(item.message);
    return stepNum === template.stepId;
  });

  return {
    stepId: template.stepId,
    message: template.message,
    eventType: apiItem?.eventType ?? "PENDING",
    isProcessed: Boolean(apiItem),
    steps: mergeSubSteps(template.steps, apiItem),
  };
};

const NegExecutionFragment: React.FC<NegExecutionFragmentProps> = ({
  enabled = true,
}) => {
  const { data, isLoading, isError, refetch } = useGetNegExecution(enabled);

  const roundNumber = useMemo(() => extractRoundNumber(data), [data]);

  /**
   * Faz o merge entre o mock (template) e os dados da API
   * - Se a etapa veio da API: isProcessed = true, usa eventType da API
   * - Se não veio: isProcessed = false, eventType = "PENDING"
   */
  const mergedSteps = useMemo((): MergedStep[] => {
    const allCompleted = isAllStepsCompleted(data);
    return NEG_EXECUTION_STEPS.map((template) =>
      mergeStep(template, data, allCompleted)
    );
  }, [data]);

  /**
   * Identifica o índice da próxima etapa a ser processada
   * (primeira etapa que ainda não foi processada)
   */
  const nextStepIndex = useMemo(() => {
    return mergedSteps.findIndex((step) => !step.isProcessed);
  }, [mergedSteps]);

  if (isLoading) {
    return <LoadingFragment show className="pt-30" />;
  }

  if (isError) {
    return (
      <Flex.Col alignItems="center" className="py-20" gap="10px">
        <span className={styles["error-message"]}>Erro ao carregar dados</span>
        <button
          className={styles["retry-button"]}
          onClick={() => void refetch()}
          type="button"
        >
          Tentar novamente
        </button>
      </Flex.Col>
    );
  }

  return (
    <Flex.Col className="py-10" data-testid="neg-execution-fragment">
      {roundNumber && (
        <div className={styles["round-header"]}>
          <span className={styles["round-header__label"]}>Rodada:</span>
          <span className={styles["round-header__number"]}>{roundNumber}</span>
        </div>
      )}

      <Flex.Col>
        {mergedSteps.map((step, index) => (
          <StepCard
            key={step.stepId}
            message={step.message}
            eventType={step.eventType}
            isProcessed={step.isProcessed}
            isNext={index === nextStepIndex}
            showDivider={index < mergedSteps.length - 1}
          >
            {step.steps && (
              <RegionalProgress
                steps={step.steps}
                parentIsNext={index === nextStepIndex}
              />
            )}
          </StepCard>
        ))}
      </Flex.Col>
    </Flex.Col>
  );
};

export default NegExecutionFragment;
